import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyPosts.css';
import './LandingPage.css';
import CreatePost from './CreatePost';
import { AppHeader, BottomNavigation, CreateOptionsMenu, EditPostPopover, ImageModal, ConfirmationModal, BadWordsModal, CommentInput, FloatingPlusButton } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
// Issue #101: Removed useGlobalModal - using NavigationContext for create pages
import { useLanguage } from '../contexts/LanguageContext';

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Issue #177: Added openCommentsForPostId prop for navigating from comment notifications
const MyPosts = ({ openCommentsForPostId, onOpenCommentsHandled }) => {
  const navigation = useNavigation();
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  // Issue #101: Removed openMarketplaceModal - using NavigationContext for create pages
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedPosts, setExpandedPosts] = useState({});
  const [posts, setPosts] = useState([]);
  const [offset, setOffset] = useState(0);
  // Issue #119: Track images array and index for gallery modal
  const [selectedImageData, setSelectedImageData] = useState(null);
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // 3-dot menu state
  const [openMenuPostId, setOpenMenuPostId] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);

  // Post image gallery state - track current image index for each post
  const [postImageIndices, setPostImageIndices] = useState({}); // { postId: currentIndex }

  // Comments functionality
  const [openCommentsPostId, setOpenCommentsPostId] = useState(null); // Track which post's comments are open
  const [comments, setComments] = useState({}); // Comments by post ID: { postId: [comments] }
  const [commentText, setCommentText] = useState({}); // Comment input text by post ID: { postId: text }
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  // Issue #67, #177: Anonymous comment state
  const [commentIsAnonymous, setCommentIsAnonymous] = useState({}); // { postId: boolean }
  // Issue #161: State for bad words modal (comments)
  const [showCommentBadWordsModal, setShowCommentBadWordsModal] = useState(false);
  const [commentBadWords, setCommentBadWords] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Format relative time for comments (consistent with LandingPage)
  const formatTimeAgo = (isoTimestamp) => {
    const now = new Date();
    const postTime = new Date(isoTimestamp);
    const diffMs = now - postTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'vor wenigen Sekunden';
    if (diffMins < 60) return `vor ${diffMins}m`;
    if (diffHours < 24) return `vor ${diffHours}h`;
    if (diffDays < 7) return `vor ${diffDays}d`;

    return postTime.toLocaleDateString('de-DE');
  };

  // Update loading state when userData is loaded from hook
  useEffect(() => {
    if (userData !== null) {
      setLoading(false);
    }
  }, [userData]);

  // Handle image modal with URL hash for back button support
  useEffect(() => {
    if (selectedImageData) {
      // Modal opened - set URL hash
      window.location.hash = 'image-modal';
    }
  }, [selectedImageData]);

  // Listen for hash changes to close modal when back button is pressed
  useEffect(() => {
    const handleHashChange = () => {
      // If modal is open and hash is no longer #image-modal, close the modal
      if (selectedImageData && window.location.hash !== '#image-modal') {
        setSelectedImageData(null);
      }
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [selectedImageData]);

  // Fetch user's own posts
  useEffect(() => {
    const fetchMyPosts = async () => {
      if (!userData) return;

      try {
        setPostsLoading(true);
        const response = await axios.get(`/api/posts/my-posts?limit=20&offset=${offset}`);
        // Filter out marketplace posts - they are shown in "Mein Marktplatz" instead
        const regularPosts = response.data.filter(post => !post.marketplace_type);
        setPosts(regularPosts);
        setError(null);
      } catch (err) {
        console.error('Error fetching my posts:', err);
        setError('Fehler beim Laden der Beiträge');
      } finally {
        setPostsLoading(false);
      }
    };

    fetchMyPosts();
  }, [userData, offset]);

  // Issue #177: Handle opening comments for a specific post from notification
  useEffect(() => {
    if (openCommentsForPostId) {
      // Open comments for the specified post
      setOpenCommentsPostId(openCommentsForPostId);
      // Fetch comments for this post
      const fetchCommentsForPost = async () => {
        setLoadingComments(true);
        try {
          const response = await axios.get(`/api/posts/${openCommentsForPostId}/comments`);
          setComments(prev => ({ ...prev, [openCommentsForPostId]: response.data }));
        } catch (err) {
          console.error('Failed to fetch comments:', err);
        } finally {
          setLoadingComments(false);
        }
      };
      fetchCommentsForPost();
      // Clear the flag
      onOpenCommentsHandled?.();
    }
  }, [openCommentsForPostId, onOpenCommentsHandled]);

  const toggleExpand = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const handlePostCreated = (newPost) => {
    // Add new post to the beginning of the list
    setPosts(prevPosts => [newPost, ...prevPosts]);
  };

  const handlePostEdited = (editedPost) => {
    // Update the edited post in the list
    setPosts(prevPosts =>
      prevPosts.map(post => post.id === editedPost.id ? editedPost : post)
    );
  };

  const handlePostDeleted = (deletedPostId) => {
    // Remove deleted post from the list
    setPosts(prevPosts => prevPosts.filter(post => post.id !== deletedPostId));
  };

  // Post image gallery navigation handlers
  const handleNextPostImage = (postId, totalImages) => {
    setPostImageIndices(prev => {
      const currentIndex = prev[postId] || 0;
      const nextIndex = (currentIndex + 1) % totalImages;
      return { ...prev, [postId]: nextIndex };
    });
  };

  // Issue #163: Wrap around - go to last image when at first
  const handlePrevPostImage = (postId, totalImages) => {
    setPostImageIndices(prev => {
      const currentIndex = prev[postId] || 0;
      const prevIndex = currentIndex === 0 ? totalImages - 1 : currentIndex - 1;
      return { ...prev, [postId]: prevIndex };
    });
  };

  // Issue #119: Open image modal with gallery support
  const openImageModal = (images, index) => {
    setSelectedImageData({ images, index });
  };

  const closeImageModal = () => {
    setSelectedImageData(null);
    // Remove hash when closing modal without scrolling to top
    if (window.location.hash === '#image-modal') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  };

  // Handle like/unlike post
  const handleLikePost = async (postId) => {
    try {
      const response = await axios.post(`/api/posts/${postId}/like`);

      // Update the post's like count and user_has_liked status in the posts list
      setPosts(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? {
              ...p,
              like_number: response.data.like_count,
              user_has_liked: response.data.user_has_liked
            }
          : p
      ));
    } catch (err) {
      console.error('Failed to like/unlike post:', err);
      alert(err.response?.data?.message || 'Fehler beim Liken des Posts');
    }
  };

  // Toggle comments section and fetch comments
  const handleToggleComments = async (postId) => {
    if (openCommentsPostId === postId) {
      // Close comments
      setOpenCommentsPostId(null);
    } else {
      // Open comments and fetch them if not already loaded
      setOpenCommentsPostId(postId);

      if (!comments[postId]) {
        setLoadingComments(true);
        try {
          const response = await axios.get(`/api/posts/${postId}/comments`);
          setComments(prev => ({ ...prev, [postId]: response.data }));
        } catch (err) {
          console.error('Failed to fetch comments:', err);
          alert(err.response?.data?.message || 'Fehler beim Laden der Kommentare');
        } finally {
          setLoadingComments(false);
        }
      }
    }
  };

  // Issue #192: Scroll to comments section when opened
  useEffect(() => {
    if (openCommentsPostId) {
      const timer = setTimeout(() => {
        const el = document.getElementById(`comments-${openCommentsPostId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openCommentsPostId]);

  // Submit a comment
  const handleSubmitComment = async (postId) => {
    const text = commentText[postId]?.trim();
    if (!text) {
      alert('Bitte gib einen Kommentar ein');
      return;
    }

    setIsSubmittingComment(true);
    try {
      // Issue #67, #177: Include is_anonymous field
      const response = await axios.post(`/api/posts/${postId}/comments`, {
        comment_text: text,
        is_anonymous: commentIsAnonymous[postId] || false
      });

      // Add new comment to the list
      setComments(prev => ({
        ...prev,
        [postId]: [response.data.comment, ...(prev[postId] || [])]
      }));

      // Clear input and reset anonymous toggle
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      setCommentIsAnonymous(prev => ({ ...prev, [postId]: false }));

      // Update comment count in posts
      setPosts(prevPosts => prevPosts.map(p =>
        p.id === postId
          ? { ...p, comment_number: p.comment_number + 1 }
          : p
      ));
    } catch (err) {
      console.error('Failed to submit comment:', err);
      // Issue #161: Check for bad words in response
      if (err.response?.data?.bad_words && err.response.data.bad_words.length > 0) {
        setCommentBadWords(err.response.data.bad_words);
        setShowCommentBadWordsModal(true);
      } else {
        alert(err.response?.data?.message || 'Fehler beim Senden des Kommentars');
      }
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Edit post handler
  const handleEditPost = (post) => {
    setEditingPost(post);
    setOpenMenuPostId(null);
  };

  // Handle save from EditPostPopover
  const handleSaveEdit = (updatedPost) => {
    setPosts(prevPosts =>
      prevPosts.map(p => p.id === updatedPost.id ? updatedPost : p)
    );
    setEditingPost(null);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingPost(null);
  };

  // Delete post handler
  const handleDeletePost = (postId) => {
    setDeletingPostId(postId);
    setOpenMenuPostId(null);
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeletingPostId(null);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    setIsDeletingPost(true);
    try {
      await axios.delete(`/api/posts/${deletingPostId}`);

      // Remove the post from the posts list
      setPosts(prevPosts => prevPosts.filter(p => p.id !== deletingPostId));

      // Close delete modal
      setDeletingPostId(null);
    } catch (err) {
      console.error('Failed to delete post:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen des Posts');
    } finally {
      setIsDeletingPost(false);
    }
  };

  // Show loading state while checking user data
  if (loading) {
    return (
      <div className="my-posts-container">
        <p className="my-posts-loading-text">Lädt...</p>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="my-posts-container">
        <p className="my-posts-error-text">{error}</p>
      </div>
    );
  }

  return (
    <div className="my-posts-container">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Create Post Component */}
      {userData && (
        <CreatePost
          userName={userData.first_name}
          onPostCreated={handlePostCreated}
        />
      )}

      {/* Tab Navigation - Single Tab for "Meine Posts" */}
      <div className="my-posts-tab-navigation">
        <button className="my-posts-tab-button active">
          {t('posts.myPosts')}
        </button>
      </div>

      {/* Posts List */}
      <div className="my-posts-feed">
        {postsLoading && <p className="my-posts-loading-text">{t('loading')}</p>}

        {!postsLoading && posts.length === 0 && (
          <p className="my-posts-empty-text">{t('posts.noOwnPosts')}</p>
        )}

        {!postsLoading && posts.map((post) => {
          const isExpanded = expandedPosts[post.id];
          const shouldShowExpandButton = post.post_content && post.post_content.length > 150;
          const displayContent = isExpanded || !shouldShowExpandButton
            ? post.post_content
            : post.post_content.substring(0, 150) + '...';

          return (
            <article key={post.id} className="post-card">
              {/* Post Header */}
              <div className="post-header">
                <div className="post-author-info">
                  <span className="author-name">{post.author_name}</span>
                  <span className="post-time">
                    {new Date(post.created_time).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                    {post.is_edited && <span className="edited-mark"> (bearbeitet)</span>}
                  </span>
                </div>
              </div>

              {/* Post Content */}
              <div className="post-content">
                {displayContent}
              </div>

              {/* Expand/Collapse Button */}
              {shouldShowExpandButton && (
                <button
                  className="expand-button"
                  onClick={() => toggleExpand(post.id)}
                >
                  {isExpanded ? 'weniger lesen ▲' : 'mehr lesen ▼'}
                </button>
              )}

              {/* Post Images */}
              {post.images && post.images.length > 0 && (
                <div className="post-images" style={{ position: 'relative' }}>
                  <div className="photo-container">
                    <img
                      src={`${API_BASE_URL}${post.images[postImageIndices[post.id] || 0]}`}
                      alt={`Post - Foto ${(postImageIndices[post.id] || 0) + 1}`}
                      className="post-image"
                      onClick={() => openImageModal(
                        post.images.map(img => `${API_BASE_URL}${img}`),
                        postImageIndices[post.id] || 0
                      )}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>

                  {/* Navigation Buttons - Only show if there are multiple images and no popover is open */}
                  {/* Issue #96: Hide arrow buttons when edit post popover is open */}
                  {/* Issue #170: Also hide when delete confirmation modal is open */}
                  {post.images.length > 1 && !editingPost && !deletingPostId && (
                    <>
                      <button
                        className="gallery-nav-button gallery-nav-left"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrevPostImage(post.id, post.images.length);
                        }}
                        aria-label="Vorheriges Bild"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '10px',
                          transform: 'translateY(-50%)',
                          zIndex: 9999,
                          pointerEvents: 'auto'
                        }}
                      >
                        &#8249;
                      </button>
                      <button
                        className="gallery-nav-button gallery-nav-right"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNextPostImage(post.id, post.images.length);
                        }}
                        aria-label="Nächstes Bild"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          right: '10px',
                          transform: 'translateY(-50%)',
                          zIndex: 9999,
                          pointerEvents: 'auto'
                        }}
                      >
                        &#8250;
                      </button>
                    </>
                  )}

                  {/* Photo Counter - Only show if there are multiple images */}
                  {post.images.length > 1 && (
                    <div className="photo-counter">
                      {(postImageIndices[post.id] || 0) + 1} / {post.images.length}
                    </div>
                  )}
                </div>
              )}

              {/* Post Actions */}
              <div className="post-footer">
                <div className="post-actions">
                  <button
                    className={`action-button ${post.user_has_liked ? 'liked' : ''}`}
                    onClick={() => handleLikePost(post.id)}
                    title={post.user_has_liked ? "Like entfernen" : "Liken"}
                  >
                    <span className="icon">{post.user_has_liked ? '👍' : '👍'}</span>
                    <span className="count">{post.like_number}</span>
                  </button>
                  <button
                    className="action-button"
                    onClick={() => handleToggleComments(post.id)}
                    title="Kommentare anzeigen"
                  >
                    <span className="icon">💬</span>
                    <span className="count">{post.comment_number}</span>
                  </button>
                  {/* 3-dot menu */}
                  {/* Issue #115: Added keyboard accessibility */}
                  {userData && (
                    <div className="post-menu">
                      <button
                        className="menu-button"
                        onClick={() => setOpenMenuPostId(openMenuPostId === post.id ? null : post.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setOpenMenuPostId(null);
                          } else if (e.key === 'ArrowDown' && openMenuPostId === post.id) {
                            e.preventDefault();
                            const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                            const firstItem = menu?.querySelector('[role="menuitem"]');
                            firstItem?.focus();
                          }
                        }}
                        title="Optionen"
                        aria-haspopup="menu"
                        aria-expanded={openMenuPostId === post.id}
                        aria-label="Mehr Optionen"
                      >
                        ⋮
                      </button>
                      {openMenuPostId === post.id && (
                        <>
                          {/* Overlay for click-away functionality */}
                          <div
                            className="post-menu-overlay"
                            onClick={() => setOpenMenuPostId(null)}
                          ></div>
                          <div className="menu-dropdown" role="menu" aria-label="Optionen">
                            <button
                              role="menuitem"
                              tabIndex={0}
                              onClick={() => handleEditPost(post)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setOpenMenuPostId(null);
                                  e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const next = e.currentTarget.nextElementSibling;
                                  if (next?.getAttribute('role') === 'menuitem') next.focus();
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const prev = e.currentTarget.previousElementSibling;
                                  if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                }
                              }}
                            >
                              {t('posts.editPost')}
                            </button>
                            <button
                              role="menuitem"
                              tabIndex={0}
                              onClick={() => handleDeletePost(post.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setOpenMenuPostId(null);
                                  e.currentTarget.closest('.post-menu').querySelector('.menu-button')?.focus();
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  const next = e.currentTarget.nextElementSibling;
                                  if (next?.getAttribute('role') === 'menuitem') next.focus();
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  const prev = e.currentTarget.previousElementSibling;
                                  if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                                }
                              }}
                              className="delete-option"
                            >
                              {t('posts.deletePost')}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Comments Section */}
              {openCommentsPostId === post.id && (
                <div className="comments-section" id={`comments-${post.id}`}>
                  {/* Issue #177: Using shared CommentInput component with anonymous toggle */}
                  <CommentInput
                    postId={post.id}
                    value={commentText[post.id] || ''}
                    onChange={(text) => setCommentText(prev => ({ ...prev, [post.id]: text }))}
                    isAnonymous={commentIsAnonymous[post.id] || false}
                    onAnonymousChange={(checked) => setCommentIsAnonymous(prev => ({ ...prev, [post.id]: checked }))}
                    onSubmit={() => handleSubmitComment(post.id)}
                    isSubmitting={isSubmittingComment}
                  />

                  {/* Comments list */}
                  <div className="comments-list">
                    {loadingComments ? (
                      <div className="comments-loading">{t('comments.loadingComments')}</div>
                    ) : comments[post.id] && comments[post.id].length > 0 ? (
                      comments[post.id].map((comment) => (
                        <div key={comment.id} className="comment-item">
                          <div className="comment-header">
                            <div className="comment-author-info">
                              {/* Issue #141: Show "(Autor)" for anonymous comments from post author */}
                              <span className="comment-author">
                                {comment.is_anonymous && comment.is_post_author
                                  ? `${comment.author_name} (${t('comments.author')})`
                                  : comment.author_name.split(' ')[0]}
                              </span>
                              <span className="comment-time">{formatTimeAgo(comment.created_at)}</span>
                            </div>
                          </div>
                          <div className="comment-text">{comment.comment_text}</div>
                        </div>
                      ))
                    ) : (
                      <div className="comments-empty">Noch keine Kommentare</div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {/* Issue #231: Floating Plus Button for quick post creation */}
      <FloatingPlusButton
        onClick={() => navigation.onNavigateToCreatePost()}
        ariaLabel={t('floatingPlusButton.createPost') || 'Neuen Beitrag erstellen'}
      />

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="home"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      {/* Issue #101: Removed onCreatePost and onCreateMarketplace props to use NavigationContext */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Edit Post Popover - Shared Component */}
      {editingPost && (
        <EditPostPopover
          post={editingPost}
          onClose={handleCancelEdit}
          onSave={handleSaveEdit}
        />
      )}

      {/* Delete Confirmation Modal - using shared ConfirmationModal component */}
      <ConfirmationModal
        isOpen={!!deletingPostId}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('posts.deletePost')}
        message={t('posts.confirmDelete')}
        confirmText={t('delete')}
        confirmingText={t('posts.deleting')}
        isConfirming={isDeletingPost}
        variant="danger"
      />

      {/* Issue #119: Image Modal with horizontal scroll navigation */}
      {selectedImageData && (
        <ImageModal
          images={selectedImageData.images}
          initialIndex={selectedImageData.index}
          onClose={closeImageModal}
        />
      )}

      {/* Issue #161: Bad words modal for comments */}
      <BadWordsModal
        isOpen={showCommentBadWordsModal}
        onClose={() => setShowCommentBadWordsModal(false)}
        badWords={commentBadWords}
        contentType="comment"
      />
    </div>
  );
};

export default MyPosts;
